import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Loader2, Settings, Key, Mail, Save, Send, Bell, BellOff, User, Eye, EyeOff, Shield, ShieldCheck, ShieldOff, Copy, RefreshCw } from "lucide-react";
import { API } from "../config/api";

export default function SettingsPage() {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    notification_email: "",
    push_notifications_enabled: false,
  });
  
  // Profile form (name and email)
  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password change form
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Password visibility toggles
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Test email
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  
  // Notification email
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Push notifications
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  
  // 2FA State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [backupCodesDialogOpen, setBackupCodesDialogOpen] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const checkPushSupport = useCallback(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    return supported;
  }, []);

  const checkPushStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/push/status`, getAuthHeader());
      setPushSubscribed(response.data.subscribed);
    } catch (error) {
      console.error("Failed to check push status:", error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchSettings();
    check2FAStatus();
    if (checkPushSupport()) {
      checkPushStatus();
    }
    // Set initial profile values from user
    if (user) {
      setProfileForm({
        name: user.name || "",
        email: user.email || "",
      });
    }
  }, [checkPushSupport, checkPushStatus, user]);

  const check2FAStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/status`, getAuthHeader());
      setTwoFactorEnabled(response.data.enabled);
    } catch (error) {
      console.error("Failed to check 2FA status:", error);
    }
  };

  const handleSetup2FA = async () => {
    setTwoFactorLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`, {}, getAuthHeader());
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setBackupCodes(response.data.backup_codes);
      setSetupDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la configurarea 2FA");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleVerify2FASetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error("Introduceți codul din 6 cifre");
      return;
    }
    
    setTwoFactorLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/verify-setup`, { code: verifyCode }, getAuthHeader());
      toast.success("Autentificarea în doi pași a fost activată!");
      setSetupDialogOpen(false);
      setTwoFactorEnabled(true);
      setVerifyCode("");
      setBackupCodesDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cod invalid");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableCode) {
      toast.error("Introduceți codul de verificare");
      return;
    }
    
    setTwoFactorLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code: disableCode }, getAuthHeader());
      toast.success("Autentificarea în doi pași a fost dezactivată");
      setDisableDialogOpen(false);
      setTwoFactorEnabled(false);
      setDisableCode("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cod invalid");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleViewBackupCodes = async () => {
    setTwoFactorLoading(true);
    try {
      const response = await axios.get(`${API}/auth/2fa/backup-codes`, getAuthHeader());
      setBackupCodes(response.data.backup_codes);
      setBackupCodesDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la încărcarea codurilor");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    const code = prompt("Introduceți codul din aplicația de autentificare:");
    if (!code) return;
    
    setTwoFactorLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/regenerate-backup-codes`, { code }, getAuthHeader());
      setBackupCodes(response.data.backup_codes);
      toast.success("Codurile de backup au fost regenerate");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cod invalid");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Codurile au fost copiate în clipboard");
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings`, getAuthHeader());
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (!profileForm.name || !profileForm.email) {
      toast.error("Numele și email-ul sunt obligatorii");
      return;
    }
    
    setSavingProfile(true);
    try {
      await axios.put(
        `${API}/auth/update-profile`,
        profileForm,
        getAuthHeader()
      );
      toast.success("Profil actualizat cu succes");
      // Refresh user data in context
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la actualizarea profilului");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Parolele noi nu coincid");
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error("Parola nouă trebuie să aibă cel puțin 6 caractere");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
        },
        getAuthHeader()
      );
      toast.success("Parola a fost schimbată cu succes");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la schimbarea parolei");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings`, settings, getAuthHeader());
      toast.success("Setările au fost salvate");
    } catch (error) {
      toast.error("Eroare la salvarea setărilor");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Introduceți adresa de email");
      return;
    }
    
    setSendingTest(true);
    try {
      await axios.post(
        `${API}/settings/test-email`,
        { email: testEmail },
        getAuthHeader()
      );
      toast.success(`Email de test trimis către ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la trimiterea email-ului");
    } finally {
      setSendingTest(false);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToPush = async () => {
    setPushLoading(true);
    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Permisiunea pentru notificări a fost refuzată");
        setPushLoading(false);
        return;
      }
      
      // Get VAPID public key
      const vapidResponse = await axios.get(`${API}/push/vapid-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to server
      const subscriptionJSON = subscription.toJSON();
      await axios.post(
        `${API}/push/subscribe`,
        {
          endpoint: subscriptionJSON.endpoint,
          keys: subscriptionJSON.keys
        },
        getAuthHeader()
      );
      
      setPushSubscribed(true);
      setSettings(prev => ({ ...prev, push_notifications_enabled: true }));
      toast.success("Notificări push activate cu succes!");
    } catch (error) {
      console.error("Push subscription error:", error);
      toast.error("Eroare la activarea notificărilor push");
    } finally {
      setPushLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setPushLoading(true);
    try {
      // Unsubscribe from server
      await axios.delete(`${API}/push/unsubscribe`, getAuthHeader());
      
      // Unsubscribe from browser
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      setPushSubscribed(false);
      setSettings(prev => ({ ...prev, push_notifications_enabled: false }));
      toast.success("Notificări push dezactivate");
    } catch (error) {
      console.error("Push unsubscription error:", error);
      toast.error("Eroare la dezactivarea notificărilor push");
    } finally {
      setPushLoading(false);
    }
  };

  const handlePushToggle = async (enabled) => {
    if (enabled) {
      await subscribeToPush();
    } else {
      await unsubscribeFromPush();
    }
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      await axios.post(`${API}/push/test`, {}, getAuthHeader());
      toast.success("Notificare push de test trimisă!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la trimiterea notificării");
    } finally {
      setTestingPush(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Acces restricționat la administratori.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <Settings className="w-8 h-8 text-primary" />
        <h1 className="font-heading text-3xl font-bold">Setări</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <User className="w-5 h-5" />
              Profil Administrator
            </CardTitle>
            <CardDescription>
              Actualizați numele și adresa de email
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="profile_name">Nume</Label>
                <Input
                  id="profile_name"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  placeholder="Administrator"
                  data-testid="input-profile-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile_email">Email (pentru autentificare)</Label>
                <Input
                  id="profile_email"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  placeholder="admin@cabinet.ro"
                  data-testid="input-profile-email"
                />
                <p className="text-xs text-muted-foreground">
                  Schimbarea email-ului va actualiza datele de autentificare
                </p>
              </div>
              <Button type="submit" disabled={savingProfile} className="w-full">
                {savingProfile ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Salvează Profil
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Key className="w-5 h-5" />
              Schimbă Parola
            </CardTitle>
            <CardDescription>
              Schimbați parola contului de administrator
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Parola curentă</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    placeholder="••••••••"
                    className="pr-10"
                    data-testid="input-current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-current-password"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">Parola nouă</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    placeholder="••••••••"
                    className="pr-10"
                    data-testid="input-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-new-password"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirmă parola nouă</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    placeholder="••••••••"
                    className="pr-10"
                    data-testid="input-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-confirm-password"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={changingPassword} className="w-full">
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Key className="w-4 h-4 mr-2" />
                )}
                Schimbă Parola
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Two-Factor Authentication */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              {twoFactorEnabled ? (
                <ShieldCheck className="w-5 h-5 text-green-600" />
              ) : (
                <Shield className="w-5 h-5" />
              )}
              Autentificare în Doi Pași (2FA)
            </CardTitle>
            <CardDescription>
              Adăugați un nivel suplimentar de securitate contului dvs.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`p-4 rounded-lg ${twoFactorEnabled ? "bg-green-50 border border-green-200" : "bg-secondary"}`}>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-body font-medium flex items-center gap-2">
                    {twoFactorEnabled ? (
                      <>
                        <ShieldCheck className="w-4 h-4 text-green-600" />
                        2FA Activat
                      </>
                    ) : (
                      <>
                        <ShieldOff className="w-4 h-4 text-muted-foreground" />
                        2FA Dezactivat
                      </>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {twoFactorEnabled 
                      ? "Contul dvs. este protejat cu autentificare în doi pași" 
                      : "Activați 2FA pentru securitate suplimentară"}
                  </p>
                </div>
              </div>
            </div>
            
            {twoFactorEnabled ? (
              <div className="space-y-2">
                <Button 
                  variant="outline" 
                  onClick={handleViewBackupCodes}
                  disabled={twoFactorLoading}
                  className="w-full"
                  data-testid="view-backup-codes-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Vezi Coduri de Backup
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => setDisableDialogOpen(true)}
                  disabled={twoFactorLoading}
                  className="w-full"
                  data-testid="disable-2fa-btn"
                >
                  <ShieldOff className="w-4 h-4 mr-2" />
                  Dezactivează 2FA
                </Button>
              </div>
            ) : (
              <Button 
                onClick={handleSetup2FA}
                disabled={twoFactorLoading}
                className="w-full"
                data-testid="setup-2fa-btn"
              >
                {twoFactorLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4 mr-2" />
                )}
                Activează 2FA
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Notification Email Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Notificări
            </CardTitle>
            <CardDescription>
              Configurați adresa de email pe care se trimit notificările
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="notification_email">Adresă email notificări</Label>
              <Input
                id="notification_email"
                type="email"
                value={settings.notification_email || ""}
                onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                placeholder="office@exemplu.ro"
                data-testid="input-notification-email"
              />
              <p className="text-xs text-muted-foreground">
                Notificările despre sarcini finalizate și sincronizări vor fi trimise la această adresă.
              </p>
            </div>
            <Button onClick={handleSaveSettings} disabled={savingSettings} className="w-full">
              {savingSettings ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvează Setările
            </Button>
          </CardContent>
        </Card>

        {/* Push Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              {pushSubscribed ? (
                <Bell className="w-5 h-5 text-success" />
              ) : (
                <BellOff className="w-5 h-5" />
              )}
              Notificări Push Browser
            </CardTitle>
            <CardDescription>
              Primiți notificări direct în browser pentru termene importante
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!pushSupported ? (
              <div className="p-4 bg-destructive/10 rounded-lg">
                <p className="text-sm text-destructive">
                  Browser-ul dvs. nu suportă notificări push.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
                  <div className="space-y-1">
                    <p className="font-body font-medium">
                      {pushSubscribed ? "Notificări active" : "Notificări inactive"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {pushSubscribed 
                        ? "Veți primi notificări pentru termene noi și sarcini" 
                        : "Activați pentru a primi alerte în browser"}
                    </p>
                  </div>
                  <Switch
                    checked={pushSubscribed}
                    onCheckedChange={handlePushToggle}
                    disabled={pushLoading}
                    data-testid="push-toggle"
                  />
                </div>
                
                {pushSubscribed && (
                  <Button 
                    variant="outline" 
                    onClick={handleTestPush} 
                    disabled={testingPush}
                    className="w-full"
                    data-testid="test-push-btn"
                  >
                    {testingPush ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Bell className="w-4 h-4 mr-2" />
                    )}
                    Trimite Notificare Test
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Test Email */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Send className="w-5 h-5" />
              Testare Email
            </CardTitle>
            <CardDescription>
              Trimiteți un email de test pentru a verifica configurația
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@exemplu.ro"
                  data-testid="input-test-email"
                />
              </div>
              <Button onClick={handleSendTestEmail} disabled={sendingTest}>
                {sendingTest ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Trimite
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 2FA Setup Dialog */}
      <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="2fa-setup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Configurare Autentificare în Doi Pași
            </DialogTitle>
            <DialogDescription>
              Scanați codul QR cu aplicația de autentificare (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* QR Code */}
            <div className="flex justify-center">
              {qrCode && (
                <img src={qrCode} alt="QR Code pentru 2FA" className="w-48 h-48 border rounded-lg" />
              )}
            </div>
            
            {/* Manual Secret */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                Sau introduceți manual acest cod în aplicație:
              </Label>
              <div className="flex gap-2">
                <Input value={secret} readOnly className="font-mono text-sm" />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(secret);
                    toast.success("Cod copiat");
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Verification Code */}
            <div className="space-y-2">
              <Label>Introduceți codul din aplicație pentru a verifica:</Label>
              <Input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="text-center text-2xl tracking-widest"
                maxLength={6}
                data-testid="2fa-verify-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupDialogOpen(false)}>
              Anulează
            </Button>
            <Button onClick={handleVerify2FASetup} disabled={twoFactorLoading || verifyCode.length !== 6}>
              {twoFactorLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4 mr-2" />
              )}
              Activează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="2fa-disable-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-destructive">
              <ShieldOff className="w-5 h-5" />
              Dezactivare 2FA
            </DialogTitle>
            <DialogDescription>
              Pentru a dezactiva autentificarea în doi pași, introduceți codul din aplicația de autentificare sau un cod de backup.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                ⚠️ Dezactivarea 2FA va face contul mai puțin sigur. Recomandăm să păstrați 2FA activat.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Cod de verificare:</Label>
              <Input
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.toUpperCase())}
                placeholder="Cod din aplicație sau backup"
                className="text-center"
                data-testid="2fa-disable-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableDialogOpen(false)}>
              Anulează
            </Button>
            <Button variant="destructive" onClick={handleDisable2FA} disabled={twoFactorLoading || !disableCode}>
              {twoFactorLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ShieldOff className="w-4 h-4 mr-2" />
              )}
              Dezactivează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={backupCodesDialogOpen} onOpenChange={setBackupCodesDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="backup-codes-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Key className="w-5 h-5" />
              Coduri de Backup
            </DialogTitle>
            <DialogDescription>
              Salvați aceste coduri într-un loc sigur. Le puteți folosi pentru a vă autentifica dacă pierdeți accesul la aplicația de autentificare.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <div 
                  key={index} 
                  className="p-2 bg-secondary rounded font-mono text-center text-sm"
                >
                  {code}
                </div>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyBackupCodes} className="flex-1">
                <Copy className="w-4 h-4 mr-2" />
                Copiază
              </Button>
              <Button variant="outline" onClick={handleRegenerateBackupCodes} disabled={twoFactorLoading} className="flex-1">
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerează
              </Button>
            </div>
            
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                ⚠️ Fiecare cod poate fi folosit o singură dată. După ce folosiți un cod, acesta devine invalid.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setBackupCodesDialogOpen(false)}>
              Am salvat codurile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
